import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../../axios/axiosHandler';
import { EndPoints } from '../../axios/endPoints';
import { authHeader } from '../../store/auth-slice/auth-slice';
import { useAppDispatch } from '../../store/store';
import { enqueueSnackbar } from '../../store/app-slice/app';
import { handleNotificationVariant } from '../notifications/_common';

interface TrainingModelShareDialogProps {
  open: boolean;
  model: any | null;
  onClose: () => void;
}

interface SharedUser {
  username: string;
  disabled: boolean;
}

const TrainingModelShareDialog: React.FC<TrainingModelShareDialogProps> = ({
  open,
  model,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [sharedUsers, setSharedUsers] = useState<SharedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [userToggleLoading, setUserToggleLoading] = useState<Record<string, boolean>>({});

  const modelId = model?.id;
  const modelName = model?.name || '';

  const sharedCount = sharedUsers.length;

  const fetchSharedUsers = useCallback(async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${EndPoints.ML_CREATOR_GET_TRAINING_MODEL_SHARED_USERS}?id=${modelId}`, authHeader());
      setSharedUsers(response.data || []);
    } catch (error) {
      setSharedUsers([]);
    } finally {
      setLoading(false);
    }
  }, [modelId]);

  useEffect(() => {
    if (open && modelId) {
      setInputError(null);
      setNewUsername('');
      fetchSharedUsers();
    } else if (!open) {
      setSharedUsers([]);
      setUserToggleLoading({});
    }
  }, [open, modelId, fetchSharedUsers]);

  const handleAddUser = async () => {
    if (!modelId) return;
    if (!newUsername.trim()) {
      setInputError(t('SHARE_MODEL_USERNAME_REQUIRED'));
      return;
    }

    setInputError(null);
    setAdding(true);
    try {
      const body = { trainingModelId: modelId, username: newUsername.trim() };
      const response = await axiosInstance.post(
        EndPoints.ML_CREATOR_SHARE_TRAINING_MODEL,
        body,
        authHeader()
      );

      if (response?.data) {
        dispatch(
          enqueueSnackbar({
            message: t(`${response.data.message || 'BE_COMMON_UNHANDLED_ERROR'}`),
            options: {
              variant: handleNotificationVariant(response.data.success),
              persist: !response.data.success,
            },
          })
        );

        if (response.data.success) {
          setNewUsername('');
          await fetchSharedUsers();
        }
      }
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: t('BE_COMMON_UNHANDLED_ERROR'),
          options: { variant: 'error', persist: true },
        })
      );
    } finally {
      setAdding(false);
    }
  };

  const handleToggleUser = async (user: SharedUser) => {
    if (!modelId) return;
    setUserToggleLoading((prev) => ({ ...prev, [user.username]: true }));

    try {
      const body = { trainingModelId: modelId, username: user.username };
      const response = await axiosInstance.post(
        EndPoints.ML_CREATOR_SHARE_TRAINING_MODEL_TOGGLE_DISABLED,
        body,
        authHeader()
      );

      if (response?.data) {
        dispatch(
          enqueueSnackbar({
            message: t(`${response.data.message || 'BE_COMMON_UNHANDLED_ERROR'}`),
            options: {
              variant: handleNotificationVariant(response.data.success),
              persist: !response.data.success,
            },
          })
        );

        if (response.data.success) {
          await fetchSharedUsers();
        }
      }
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: t('BE_COMMON_UNHANDLED_ERROR'),
          options: { variant: 'error', persist: true },
        })
      );
    } finally {
      setUserToggleLoading((prev) => ({ ...prev, [user.username]: false }));
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddUser();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('SHARE_MODEL_DIALOG_TITLE', { name: modelName })}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('SHARE_MODEL_DIALOG_INFO')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label={t('SHARE_MODEL_INPUT_LABEL')}
            value={newUsername}
            fullWidth
            size="small"
            onChange={(e) => {
              setNewUsername(e.target.value);
              if (inputError) {
                setInputError(null);
              }
            }}
            onKeyDown={handleKeyDown}
            error={!!inputError}
            helperText={inputError || ' '}
          />
          <Button
            variant="contained"
            className="button-color"
            onClick={handleAddUser}
            disabled={adding}
          >
            {adding ? <CircularProgress color="inherit" size={20} /> : t('SHARE_MODEL_ADD_BUTTON')}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">{t('SHARE_MODEL_LIST_TITLE')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('SHARE_MODEL_SHARED_COUNT', { count: sharedCount })}
          </Typography>
        </Box>

        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            maxHeight: 280,
            overflowY: 'auto',
            px: 1,
            py: 0.5,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : sharedUsers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
              {t('SHARE_MODEL_NO_USERS')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {sharedUsers.map((user, index) => (
                <ListItem
                  key={user.username}
                  divider={index < sharedUsers.length - 1}
                  secondaryAction={
                    <Switch
                      edge="end"
                      onChange={() => handleToggleUser(user)}
                      checked={!user.disabled}
                      disabled={!!userToggleLoading[user.username]}
                      inputProps={{ 'aria-label': t('SHARE_MODEL_TOGGLE_LABEL') }}
                    />
                  }
                >
                  <ListItemText
                    primary={user.username}
                    secondary={user.disabled ? t('SHARE_MODEL_DISABLED_LABEL') : t('SHARE_MODEL_ENABLED_LABEL')}
                  />
                  {userToggleLoading[user.username] ? <CircularProgress size={16} sx={{ ml: 1 }} /> : null}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('CLOSE_BUTTON')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrainingModelShareDialog;
